import { convertToObject } from './formUtils';

/**
 * Validates and updates a form field value as a number, ensuring it meets certain constraints.
 *
 * @param {Object} form - The Ant Design form instance used to set the field values.
 * @param {Object} event - The input change event, containing the target value.
 * @param {string} name - The name of the form field to be updated.
 * @param {boolean} [max=false] - Optional flag to enforce a maximum value of 100.
 * @param {number} [min=0] - Optional minimum value constraint.
 *
 * This function ensures that:
 * - The input is converted to a number.
 * - If the input is not a valid number, it defaults to 0.
 * - Values less than `min` are adjusted to `min`.
 * - If `max` is true, values greater than 100 are capped at 100.
 *
 * Example usage:
 * ```javascript
 * import { formValidateAndSetNumber } from './utils';
 *
 * const handleChange = (event) => {
 *   formValidateAndSetNumber(form, event, 'fieldName', true, 10);
 * };
 * ```
 */
export const formValidateAndSetNumber = (
  form,
  event,
  name,
  max = false,
  min = 0
) => {
  const rawValue = event.target.value;

  // Convert value to a number
  const numValue = Number(rawValue);

  // If the value is not a number, default to `min`
  if (isNaN(numValue)) {
    const setValue = convertToObject(name, min);
    form.setFieldsValue(setValue);
    return;
  }

  // Only override the form value when clamping is needed
  let clamped = numValue;
  if (numValue < min) clamped = min;
  if (max && numValue > 100) clamped = 100;

  if (clamped !== numValue) {
    const setValue = convertToObject(name, clamped);
    form.setFieldsValue(setValue);
  }
};

/**
 * Validates a password to ensure it meets specific security requirements:
 * - At least one uppercase letter.
 * - At least one digit.
 * - At least one special character.
 * - Minimum length of 8 characters.
 *
 * @param {string} _ - Unused parameter (standard for Ant Design's validation).
 * @param {string} value - The password to be validated.
 * @param {function} t - Translation function for error messages.
 * @returns {Promise} A promise that resolves if the password is valid or rejects with an error message.
 */
export const validatePassword = (_, value, t) => {
  // If the password is empty, reject with an appropriate error message
  if (!value) {
    return Promise.reject();
  }

  // Regex to validate password requirements
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,}$/;

  // If the password doesn't match the regex, reject with a password requirement message
  if (!passwordRegex.test(value)) {
    return Promise.reject(
      t('formValidation.passwordRequirements', { ns: 'forms' })
    );
  }

  // Resolve if the password is valid
  return Promise.resolve();
};

/**
 * Validates a phone number to ensure it meets the length and format constraints.
 * The phone number must be between 6 and 15 digits long and contain only numbers.
 *
 * @param {string} _ - Unused parameter (standard for Ant Design's validation).
 * @param {string} value - The phone number to be validated.
 * @param {function} t - Translation function for error messages.
 * @returns {Promise} A promise that resolves if the phone number is valid or rejects with an error message.
 */
export const validatePhoneNumber = (_, value, t) => {
  // If the phone number is empty, resolve (no validation needed)
  if (!value) {
    return Promise.resolve();
  }

  // Convert to string to handle cases where Input type="number" returns a number
  const valueStr = String(value);

  // Check if the value contains only digits
  const digitPattern = /^[0-9]+$/;
  if (!digitPattern.test(valueStr)) {
    return Promise.reject(
      new Error(t('formValidation.numberField.invalid', { ns: 'forms' }))
    );
  }

  // If the phone number length is outside the range of 6 to 15 characters, reject with an error
  if (valueStr.length < 6 || valueStr.length > 15) {
    return Promise.reject(
      new Error(t('formValidation.numberField.range', { min: 6, max: 15 }))
    );
  }

  // Resolve if the phone number is valid
  return Promise.resolve();
};
